
import { useMutation } from "@tanstack/react-query";
import { toast } from "sonner";
import { format } from "date-fns";
import type { Candidate } from "@/components/candidates/types";

interface ExportData {
  candidates: Candidate[];
  dateRange?: { from: Date; to: Date };
  format: 'csv' | 'pdf';
}

export const useAnalyticsExport = () => {
  const exportMutation = useMutation({
    mutationFn: async ({ candidates, dateRange, format: exportFormat }: ExportData) => {
      // Simulate export processing
      await new Promise(resolve => setTimeout(resolve, 2000));
      
      if (exportFormat === 'csv') {
        // Generate CSV data
        const csvData = generateCSVExport(candidates, dateRange);
        downloadCSV(csvData, generateFilename('csv', dateRange));
      } else {
        // For PDF, we would integrate with a PDF generation library
        // For now, we'll create a structured data export
        const reportData = generateReportData(candidates, dateRange);
        downloadJSON(reportData, generateFilename('json', dateRange));
      }
      
      return { success: true };
    },
    onSuccess: () => {
      toast.success("Analytics data exported successfully");
    },
    onError: () => {
      toast.error("Failed to export analytics data");
    }
  });

  return {
    exportData: exportMutation.mutate,
    isExporting: exportMutation.isPending
  };
};

const generateCSVExport = (candidates: Candidate[], dateRange?: { from: Date; to: Date }) => {
  const headers = [
    'Name',
    'Email',
    'Status',
    'Source',
    'Experience Years',
    'Created Date',
    'Last Updated'
  ];

  const rows = candidates.map(candidate => [
    `${candidate.first_name || ''} ${candidate.last_name || ''}`.trim(),
    candidate.email || '',
    candidate.status || '',
    candidate.source || '',
    candidate.experience_years?.toString() || '',
    candidate.created_at ? format(new Date(candidate.created_at), 'yyyy-MM-dd') : '',
    candidate.updated_at ? format(new Date(candidate.updated_at), 'yyyy-MM-dd') : ''
  ]);

  return [headers, ...rows].map(row => row.map(cell => `"${cell}"`).join(',')).join('\n');
};

const generateReportData = (candidates: Candidate[], dateRange?: { from: Date; to: Date }) => {
  return {
    metadata: {
      exportDate: format(new Date(), 'yyyy-MM-dd HH:mm:ss'),
      dateRange: dateRange ? {
        from: format(dateRange.from, 'yyyy-MM-dd'),
        to: format(dateRange.to, 'yyyy-MM-dd')
      } : null,
      totalCandidates: candidates.length
    },
    summary: {
      totalCandidates: candidates.length,
      hiredCount: candidates.filter(c => c.status === 'hired').length,
      byStatus: candidates.reduce((acc, candidate) => {
        const status = candidate.status || 'unknown';
        acc[status] = (acc[status] || 0) + 1;
        return acc;
      }, {} as Record<string, number>),
      bySource: candidates.reduce((acc, candidate) => {
        const source = candidate.source || 'unknown';
        acc[source] = (acc[source] || 0) + 1;
        return acc;
      }, {} as Record<string, number>)
    },
    candidates: candidates.map(candidate => ({
      id: candidate.id,
      name: `${candidate.first_name || ''} ${candidate.last_name || ''}`.trim(),
      email: candidate.email,
      status: candidate.status,
      source: candidate.source,
      experienceYears: candidate.experience_years,
      createdAt: candidate.created_at,
      updatedAt: candidate.updated_at
    }))
  };
};

const downloadCSV = (csvContent: string, filename: string) => {
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');
  const url = URL.createObjectURL(blob);
  link.setAttribute('href', url);
  link.setAttribute('download', filename);
  link.style.visibility = 'hidden';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

const downloadJSON = (data: any, filename: string) => {
  const jsonContent = JSON.stringify(data, null, 2);
  const blob = new Blob([jsonContent], { type: 'application/json;charset=utf-8;' });
  const link = document.createElement('a');
  const url = URL.createObjectURL(blob);
  link.setAttribute('href', url);
  link.setAttribute('download', filename);
  link.style.visibility = 'hidden';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

const generateFilename = (fileExtension: string, dateRange?: { from: Date; to: Date }) => {
  const dateString = dateRange 
    ? `${format(dateRange.from, 'yyyy-MM-dd')}_to_${format(dateRange.to, 'yyyy-MM-dd')}`
    : format(new Date(), 'yyyy-MM-dd');
  return `analytics_export_${dateString}.${fileExtension}`;
};
